import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Edit, Trash2, Loader2, Package, Plus, Minus, IndianRupee, 
  BarChart3, ClipboardList
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Kits", "Puris", "Spices", "Chutneys", "Toppings", "Fillings", "Supplies", "Equipment"];

export default function AdminCatalogTab({ 
  products, 
  productRecipes, 
  rawMaterials,
  isLoading, 
  onRefresh, 
  onRefreshRecipes 
}) {
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
  const [isStockDialogOpen, setIsStockDialogOpen] = useState(false);
  const [isRecipeDialogOpen, setIsRecipeDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProductForRecipe, setSelectedProductForRecipe] = useState(null);
  const [recipeIngredients, setRecipeIngredients] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stockAdjustment, setStockAdjustment] = useState({ adjustment: 0, reason: "" });
  const [productFormData, setProductFormData] = useState({
    name: "", description: "", price: "", category: "Kits",
    image_url: "", stock_quantity: "", sku: "", min_stock_alert: "10"
  });

  const openAddProductDialog = () => {
    setEditingProduct(null);
    setProductFormData({
      name: "", description: "", price: "", category: "Kits",
      image_url: "", stock_quantity: "", sku: "", min_stock_alert: "10"
    });
    setIsProductDialogOpen(true);
  };

  const openEditProductDialog = (e) => {
    const productId = e.currentTarget.getAttribute('data-product-id');
    const product = products.find(p => p.product_id === productId);
    if (!product) {
      toast.error("Could not find product");
      return;
    }
    setEditingProduct(productId);
    setProductFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      image_url: product.image_url || "",
      stock_quantity: (product.stock_quantity || 0).toString(),
      sku: product.sku || "",
      min_stock_alert: (product.min_stock_alert || 10).toString()
    });
    setIsProductDialogOpen(true);
  };

  const openStockDialog = (product) => {
    setEditingProduct(product);
    setStockAdjustment({ adjustment: 0, reason: "" });
    setIsStockDialogOpen(true);
  };

  const handleProductSubmit = async () => {
    if (!productFormData.name || !productFormData.price) {
      toast.error("Name and price are required");
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        name: productFormData.name,
        description: productFormData.description,
        price: parseFloat(productFormData.price),
        category: productFormData.category,
        image_url: productFormData.image_url || null,
        stock_quantity: parseInt(productFormData.stock_quantity) || 0,
        sku: productFormData.sku || null,
        min_stock_alert: parseInt(productFormData.min_stock_alert) || 10,
        in_stock: (parseInt(productFormData.stock_quantity) || 0) > 0
      };

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct}`, payload);
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products`, payload);
        toast.success("Product created");
      }
      setIsProductDialogOpen(false);
      setEditingProduct(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Delete this product permanently?")) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success("Product deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const handleStockAdjust = async () => {
    if (stockAdjustment.adjustment === 0) {
      toast.error("Enter a non-zero adjustment");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/admin/products/${editingProduct.product_id}/adjust-stock`, {
        adjustment: stockAdjustment.adjustment,
        reason: stockAdjustment.reason || "Manual adjustment"
      });
      toast.success("Stock adjusted");
      setIsStockDialogOpen(false);
      setEditingProduct(null);
      onRefresh();
    } catch (error) {
      toast.error("Failed to adjust stock");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Recipe functions
  const openRecipeDialog = async (product) => {
    setSelectedProductForRecipe(product);
    try {
      const res = await axios.get(`${API}/ops-extended/product-recipes/${product.product_id}`);
      if (res.data.has_recipe && res.data.ingredients) {
        setRecipeIngredients(res.data.ingredients.map(ing => ({
          raw_material_id: ing.raw_material_id,
          raw_material_name: ing.raw_material_name,
          quantity: ing.quantity,
          unit: ing.unit
        })));
      } else {
        setRecipeIngredients([]);
      }
    } catch (error) {
      setRecipeIngredients([]);
    }
    setIsRecipeDialogOpen(true);
  };

  const addIngredientToRecipe = () => {
    setRecipeIngredients([...recipeIngredients, { raw_material_id: "", quantity: 0 }]);
  };

  const removeIngredientFromRecipe = (index) => {
    setRecipeIngredients(recipeIngredients.filter((_, i) => i !== index));
  };

  const updateIngredient = (index, field, value) => {
    const updated = [...recipeIngredients];
    updated[index][field] = value;
    if (field === "raw_material_id") {
      const rm = rawMaterials.find(r => r.raw_material_id === value);
      if (rm) {
        updated[index].raw_material_name = rm.name;
        updated[index].unit = rm.unit;
      }
    }
    setRecipeIngredients(updated);
  };

  const handleSaveRecipe = async () => {
    const validIngredients = recipeIngredients.filter(ing => ing.raw_material_id && ing.quantity > 0);
    if (validIngredients.length === 0) {
      toast.error("Add at least one ingredient with quantity > 0");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/ops-extended/product-recipes`, {
        product_id: selectedProductForRecipe.product_id,
        ingredients: validIngredients.map(ing => ({
          raw_material_id: ing.raw_material_id,
          quantity: ing.quantity
        }))
      });
      toast.success("Recipe saved successfully");
      setIsRecipeDialogOpen(false);
      onRefreshRecipes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save recipe");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 mb-6">
        <Card className="border-0 shadow-card">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Products</p>
                <p className="text-2xl lg:text-3xl font-heading mt-1">{products.length}</p>
              </div>
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-accent/10 rounded-xl flex items-center justify-center">
                <Package className="w-5 h-5 lg:w-6 lg:h-6 text-accent" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-card">
          <CardContent className="p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In Stock</p>
                <p className="text-2xl lg:text-3xl font-heading mt-1 text-green-600">
                  {products.filter(p => p.in_stock).length}
                </p>
              </div>
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Package className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-card">
          <CardContent className="p-4 lg:p-6 flex items-center justify-center">
            <Button onClick={openAddProductDialog} className="rounded-full" data-testid="add-product-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Products Table */}
      <Card className="border-0 shadow-card overflow-hidden" data-testid="products-table-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg lg:text-xl">Product Catalog</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0 sm:p-6">
          <div className="w-full overflow-x-auto">
            <Table className="min-w-[600px]">
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead className="hidden sm:table-cell">Category</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead className="hidden sm:table-cell">Stock</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.product_id} data-testid={`product-row-${p.product_id}`}>
                    <TableCell className="font-mono text-xs">{p.sku || "-"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {p.image_url && (
                          <img src={p.image_url} alt={p.name} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="font-semibold text-xs sm:text-sm truncate max-w-[100px] sm:max-w-[150px]">{p.name}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant="outline" className="rounded-full text-xs">{p.category}</Badge>
                    </TableCell>
                    <TableCell className="font-semibold text-xs sm:text-sm">
                      <span className="flex items-center">
                        <IndianRupee className="w-3 h-3" />{p.price}
                      </span>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <span className={`font-semibold ${(p.stock_quantity || 0) <= (p.min_stock_alert || 10) ? 'text-yellow-600' : ''}`}>
                        {p.stock_quantity || 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`rounded-full text-xs ${p.in_stock ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {p.in_stock ? "In" : "Out"}
                      </Badge>
                      {productRecipes.find(r => r.product_id === p.product_id) && (
                        <Badge className="ml-1 bg-blue-100 text-blue-700 text-xs">Recipe</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => openRecipeDialog(p)} 
                        title="Manage Recipe"
                        className={productRecipes.find(r => r.product_id === p.product_id) ? "text-blue-600" : "text-muted-foreground"}
                      >
                        <ClipboardList className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openStockDialog(p)} title="Adjust Stock">
                        <BarChart3 className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        data-product-id={p.product_id}
                        onClick={openEditProductDialog}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteProduct(p.product_id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Product Dialog */}
      <Dialog open={isProductDialogOpen} onOpenChange={setIsProductDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingProduct ? "Edit Product" : "Add New Product"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>Product Name *</Label>
              <Input
                data-testid="product-name-input"
                value={productFormData.name}
                onChange={(e) => setProductFormData({ ...productFormData, name: e.target.value })}
                placeholder="Classic Panipuri Kit"
              />
            </div>
            <div className="space-y-2">
              <Label>SKU (auto-generated if empty)</Label>
              <Input
                value={productFormData.sku}
                onChange={(e) => setProductFormData({ ...productFormData, sku: e.target.value })}
                placeholder="SKU-MP0001"
              />
            </div>
            <div className="col-span-1 sm:col-span-2 space-y-2">
              <Label>Description</Label>
              <Textarea
                value={productFormData.description}
                onChange={(e) => setProductFormData({ ...productFormData, description: e.target.value })}
                placeholder="Product description..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Price (₹) *</Label>
              <Input
                data-testid="product-price-input"
                type="number"
                value={productFormData.price}
                onChange={(e) => setProductFormData({ ...productFormData, price: e.target.value })}
                placeholder="299"
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={productFormData.category}
                onValueChange={(v) => setProductFormData({ ...productFormData, category: v })}
              >
                <SelectTrigger data-testid="product-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Stock Quantity</Label>
              <Input
                data-testid="product-stock-input"
                type="number"
                value={productFormData.stock_quantity}
                onChange={(e) => setProductFormData({ ...productFormData, stock_quantity: e.target.value })}
                placeholder="100"
              />
            </div>
            <div className="space-y-2">
              <Label>Low Stock Alert Threshold</Label>
              <Input
                type="number"
                value={productFormData.min_stock_alert}
                onChange={(e) => setProductFormData({ ...productFormData, min_stock_alert: e.target.value })}
                placeholder="10"
              />
            </div>
            <div className="col-span-1 sm:col-span-2 space-y-2">
              <Label>Image URL</Label>
              <Input
                value={productFormData.image_url}
                onChange={(e) => setProductFormData({ ...productFormData, image_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setIsProductDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={handleProductSubmit} disabled={isSubmitting} className="rounded-full" data-testid="save-product-btn">
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingProduct ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock Adjustment Dialog */}
      <Dialog open={isStockDialogOpen} onOpenChange={setIsStockDialogOpen}>
        <DialogContent data-testid="stock-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Adjust Stock</DialogTitle>
          </DialogHeader>
          {editingProduct && (
            <div className="py-4 space-y-4">
              <div className="flex items-center gap-4 p-4 bg-muted rounded-xl">
                {editingProduct.image_url && (
                  <img src={editingProduct.image_url} alt={editingProduct.name} className="w-16 h-16 rounded-lg object-cover" />
                )}
                <div>
                  <p className="font-semibold">{editingProduct.name}</p>
                  <p className="text-sm text-muted-foreground">Current stock: <strong>{editingProduct.stock_quantity || 0}</strong></p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Adjustment (+/-)</Label>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-full"
                    onClick={() => setStockAdjustment({ ...stockAdjustment, adjustment: stockAdjustment.adjustment - 10 })}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <Input
                    type="number"
                    value={stockAdjustment.adjustment}
                    onChange={(e) => setStockAdjustment({ ...stockAdjustment, adjustment: parseInt(e.target.value) || 0 })}
                    className="text-center text-xl font-bold w-24"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-full"
                    onClick={() => setStockAdjustment({ ...stockAdjustment, adjustment: stockAdjustment.adjustment + 10 })}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground text-center">
                  New stock: <strong className={stockAdjustment.adjustment >= 0 ? "text-green-600" : "text-red-600"}>
                    {Math.max(0, (editingProduct.stock_quantity || 0) + stockAdjustment.adjustment)}
                  </strong>
                </p>
              </div>
              <div className="space-y-2">
                <Label>Reason (optional)</Label>
                <Input
                  value={stockAdjustment.reason}
                  onChange={(e) => setStockAdjustment({ ...stockAdjustment, reason: e.target.value })}
                  placeholder="e.g., New shipment received"
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setIsStockDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={handleStockAdjust} disabled={isSubmitting} className="rounded-full" data-testid="save-stock-btn">
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Adjust Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recipe Dialog */}
      <Dialog open={isRecipeDialogOpen} onOpenChange={setIsRecipeDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-600" />
              Product Recipe
            </DialogTitle>
          </DialogHeader>
          {selectedProductForRecipe && (
            <div className="py-4 space-y-4">
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="font-semibold">{selectedProductForRecipe.name}</p>
                <p className="text-sm text-muted-foreground">
                  Define raw materials needed per unit of this product
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Ingredients</Label>
                  <Button variant="outline" size="sm" onClick={addIngredientToRecipe}>
                    <Plus className="w-4 h-4 mr-1" /> Add Ingredient
                  </Button>
                </div>

                {recipeIngredients.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No ingredients defined. Click "Add Ingredient" to start.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {recipeIngredients.map((ing, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 border rounded-lg">
                        <Select 
                          value={ing.raw_material_id} 
                          onValueChange={(v) => updateIngredient(idx, "raw_material_id", v)}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Select raw material" />
                          </SelectTrigger>
                          <SelectContent>
                            {rawMaterials.map((rm) => (
                              <SelectItem key={rm.raw_material_id} value={rm.raw_material_id}>
                                {rm.name} ({rm.unit})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={ing.quantity}
                          onChange={(e) => updateIngredient(idx, "quantity", parseFloat(e.target.value) || 0)}
                          className="w-24"
                          placeholder="Qty"
                        />
                        <span className="text-sm text-muted-foreground w-12">{ing.unit || ""}</span>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-red-500"
                          onClick={() => removeIngredientFromRecipe(idx)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> When this product is dispatched, these raw materials 
                  will be automatically deducted from the kitchen's inventory.
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setIsRecipeDialogOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleSaveRecipe} 
              disabled={isSubmitting}
              className="rounded-full bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Recipe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
